const escapeVarNameForRegex = (name) =>
  String(name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const buildVariablePlaceholderRegex = (name, flags = 'gi') =>
  new RegExp(`\\{\\{\\s*${escapeVarNameForRegex(name)}\\s*\\}\\}`, flags);

export const buildPromptWithVariables = (baseText, variables = [], variableValues = {}) => {
  if (!baseText || !Array.isArray(variables) || variables.length === 0) return baseText || '';

  let result = baseText;

  variables.forEach((variable) => {
    if (!variable || !variable.name) return;
    const raw = variableValues && Object.prototype.hasOwnProperty.call(variableValues, variable.name)
      ? variableValues[variable.name]
      : undefined;
    const hasValue = raw !== undefined && raw !== null && String(raw).trim() !== '';
    const replacementValue = hasValue ? String(raw) : (variable.defaultValue || `{{${variable.name}}}`);

    const pattern = buildVariablePlaceholderRegex(variable.name);
    result = result.replace(pattern, () => replacementValue);
  });

  return result;
};

export const templateUsesVariable = (baseText, varName) => {
  if (!baseText || !varName) return false;
  return buildVariablePlaceholderRegex(varName).test(baseText);
};

// Metadata inputs may not have an inline placeholder in older capabilities.
// Keep them in the execution payload without changing the saved AI prompt.
export const buildExecutionPromptWithVariables = (baseText, variables = [], variableValues = {}) => {
  const prompt = buildPromptWithVariables(baseText, variables, variableValues);
  const inputs = Object.fromEntries((Array.isArray(variables) ? variables : [])
    .filter((variable) => variable?.name && !templateUsesVariable(baseText, variable.name))
    .map((variable) => {
      const raw = variableValues?.[variable.name];
      return [variable.name, raw !== undefined && raw !== null && String(raw).trim() !== '' ? raw : variable.defaultValue ?? ''];
    })
    .filter(([, value]) => String(value).trim() !== ''));
  return Object.keys(inputs).length
    ? `${prompt}\n\nRun inputs (JSON data supplied for this execution):\n${JSON.stringify(inputs, null, 2)}`
    : prompt;
};

export const getMissingVariablesForTemplate = (baseText, variables = [], variableValues = {}) => {
  if (!Array.isArray(variables) || variables.length === 0) return [];
  return variables.filter((variable) => {
    if (!variable?.name || !templateUsesVariable(baseText, variable.name)) return false;
    const raw = variableValues && Object.prototype.hasOwnProperty.call(variableValues, variable.name)
      ? variableValues[variable.name]
      : undefined;
    const hasValue = raw !== undefined && raw !== null && String(raw).trim() !== '';
    if (hasValue) return false;
    return true;
  });
};

export const extractVariableNamesFromTemplate = (baseText = '') => {
  const names = [];
  const seen = new Set();
  const pattern = /\{\{\s*([^}]+?)\s*\}\}/g;
  let match;
  while ((match = pattern.exec(String(baseText || ''))) !== null) {
    const name = match[1].trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      names.push(name);
    }
  }
  return names;
};

export const mergeVariablesFromTemplate = (baseText = '', variables = []) => {
  const list = Array.isArray(variables) ? variables : [];
  const existing = new Set(list.map((variable) => String(variable?.name || '').toLowerCase()).filter(Boolean));
  const additions = extractVariableNamesFromTemplate(baseText)
    .filter((name) => !existing.has(name.toLowerCase()))
    .map((name) => ({
      name,
      description: '',
      defaultValue: '',
      required: true,
    }));
  return additions.length ? [...list, ...additions] : list;
};
